import { useLayerStore } from '@/store/use-layer-store';
import getRootAst, { getAstColorTable } from './rootAst';
import { Shape, ShapeType } from '@/services/shape';
import { animationToLayoutAst, shapesToLayoutAst } from './layouts';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { Description } from '@/types/watchface';
import {
  addResourceIfNotExists,
  insertImages,
  removeColorGroupWithWidgetImageResource,
  removeCustomAttributes,
  removeDuplicateLayout,
  sortResource
} from './utils/resource.utils';
import { getRefName } from './utils/name.utils';
import { useTranslationStore } from '@/store/use-translation-store';
import { toArgb } from '@/watch-renderer/utils/color';
import { canUse } from '../can-use.utils';
import { useImagesStore } from '@/store/use-images-store';
import { ImageFormatKey } from '@/resources/image-format';
import { unifyTimeSecond } from './utils/data-source.utils';
import { useFormatsStore } from '@/store/use-formats-store';
import generateID from '../generate-id';
import { addWatchfaceNameTranslation } from './utils/translation.utils';
import { addThemeDefaultBg } from './utils/theme-bg.utils';
import { getTextWatchfaceAst } from './layouts/text-watchface';
import { getVideoWatchfaceAst } from './layouts/video-watchface';
import { PhotoAlbumBg } from '@/services/shape/attributes/photo-album-bg';

const _addColorTable = (
  ast: Ast,
  colorTable: string[],
  colorMode: 'colorGroup' | 'recolor'
) => {
  if ((colorTable?.length ?? 1) <= 1) return;

  colorTable = [...new Set(colorTable)];
  const _colorTable = colorTable.join(',');
  if (colorMode == 'colorGroup') {
    ast.attrsMap['colorGroupTable'] = _colorTable;
  } else {
    ast.attrsMap['recolorTable'] = _colorTable;
  }
  return colorTable;
};

// 处理相册或人像表盘
const _handlePhotoAlbumOrPortraitTheme = (
  themeAst: Ast,
  resourceAst: Ast,
  shapes: Shape[]
) => {
  themeAst.attrsMap['isPhotoAlbumWatchface'] = 'true';

  const shape = shapes.find((item) => item.type == ShapeType.PhotoAlbumBg);
  if (!shape) return;

  // 如果隐藏已不显示
  if (buildAstState.options.buildFor == 'editor' && !shape.attrs._visibility)
    return;

  const attrs = shape.attrs as PhotoAlbumBg;
  const { resources, alpha = 1 } = attrs;

  const isMultiColor = themeAst.attrsMap.recolorTable?.split(',')?.length > 1;
  const imageMap = insertImages(resourceAst, resources, {
    recolorEnable: isMultiColor
      ? shape.attrs.recolorEnable
        ? true
        : false
      : false,
    alpha
  });
  // 添加背景图
  const bgRef = getRefName(imageMap['photoAlbumBg']);
  themeAst.attrsMap['bg'] = bgRef;
  // 根据协议要求，人像表盘背景需要在layout中增加一个图层
  if (bgRef) {
    themeAst.children.unshift({
      tag: 'Layout',
      attrsMap: {
        ref: bgRef,
        x: '0',
        y: '0',
        _id: generateID(),
        _name: '人像表盘背景图片',
        _alpha: String(alpha)
      },
      children: []
    });
  }

  // 添加前景图
  const fgRef = getRefName(imageMap['photoAlbumFg']);
  themeAst.attrsMap['fg'] = fgRef;
  // 按照协议前景图不需要再theme中增加图片，但是在editor中需要增加一个图层
  if (fgRef && buildAstState.options.buildFor == 'editor') {
    themeAst.children.push({
      tag: 'Layout',
      attrsMap: {
        ref: fgRef,
        x: '0',
        y: '0',
        _id: generateID(),
        _name: '人像表盘前景图片',
        _alpha: String(alpha)
      },
      children: []
    });
  }
};

const _handleVideoWatchface = (
  themeAst: Ast,
  resourceAst: Ast,
  shapes: Shape[]
) => {
  const shape = shapes.find((item) => item.type == ShapeType.VideoWatchface);
  if (!shape) return;
  // 如果隐藏已不显示
  if (buildAstState.options.buildFor == 'editor' && !shape.attrs._visibility)
    return;

  const videoLayoutAst = getVideoWatchfaceAst(resourceAst, shape);
  // 和isPhotoAlbumWatchface互斥
  themeAst.attrsMap['isVideoAlbumWatchface'] = 'true';
  themeAst.attrsMap['bg'] = videoLayoutAst.attrsMap.ref;
  // 将videoLayoutAst添加到themeAst的children中
  themeAst.children.unshift(videoLayoutAst);
};

// 处理文字表盘
const _handleTextwatchfaceTheme = (
  themeAst: Ast,
  resourceAst: Ast,
  shapes: Shape[]
) => {
  const shape = shapes.find((item) => item.type == ShapeType.TextWatchface);
  if (!shape) return;
  const layout = getTextWatchfaceAst(resourceAst, shape);
  themeAst.children.unshift(layout);
};

// 添加预览图
const _addThemePreview = (
  theme: StoreThemeItem,
  themeAst: Ast,
  resourceAst: Ast
) => {
  const preview = theme.preview;
  if (!preview) return;

  const ast: Ast = {
    tag: 'Image',
    attrsMap: {
      src: preview,
      compressMethod: buildAstState.data.compressMethod,
      format: buildAstState.data.imageFormatDefault
    },
    children: []
  };
  const ast2 = addResourceIfNotExists(resourceAst, ast);
  themeAst.attrsMap['preview'] = getRefName(ast2.attrsMap.name);
};

interface BuildAstOptions {
  themeName?: string;
  themeType?: string;
  buildFor: 'editor' | 'export';
  exportForApp?: boolean;
  keepUslessAttr?: boolean;
  addDefaultBgImage?: boolean;
}
interface BuildAstData {
  description?: Description;
  colorMode?: 'colorGroup' | 'recolor';
  theme?: StoreThemeItem;
  translations?: Record<string, string>;
  colorTable?: string[];
  watchfaceType: Required<Description>['watchfaceType'];
  /**
   * 多语言
   */
  locales: string[];
  /** 图片压缩格式 */
  autoImageFormats?: Record<string, ImageFormatKey>;
  imageFormatDefault: ImageFormatKey;
  imagesFormatDefault: ImageFormatKey;
  /** 压缩方式：表盘整体以及图片，图组 */
  compressMethod: string;
  getImgSize: (src: string) => { width: number; height: number };
  imageArrayEmptyEnabled: boolean;

  /** 是否是安卓智能设备：即大核 */
  isAndroidDevice: boolean;

  isMultiSprite: boolean;
}

export const buildAstState = {
  options: {} as BuildAstOptions,
  data: {} as BuildAstData
};

/**
 * 生成ast数据
 * @returns
 */
export const buildAst = (options: BuildAstOptions) => {
  buildAstState.options = {
    ...options
  };
  // 配置表盘信息
  const layerStore = useLayerStore();
  const watchfaceStore = useWatchfaceStore();
  const formatStore = useFormatsStore();
  const imageStore = useImagesStore();

  const description = watchfaceStore.description as Description;
  const themes = layerStore.themes as StoreThemeItem[];
  const colorMode = watchfaceStore.colorMode;
  const watchfaceType = description.watchfaceType ?? 'normal';
  const translationStore = useTranslationStore();

  const canUseIndexedColor = canUse(
    'indexed-color',
    description.deviceType
  ).valid;
  const canUseMultiLocals = canUse(
    'multi-locals',
    description.deviceType
  ).valid;

  buildAstState.data = {
    description,
    colorMode,
    watchfaceType,
    locales: canUseMultiLocals ? [...translationStore.$state.locales] : [],
    autoImageFormats: formatStore.formats,
    // 先设置为不压缩，根据ast值修改
    compressMethod: 'None',
    getImgSize: (src: string) => {
      const { width = 0, height = 0 } = imageStore.imagesMap[src] ?? {};
      return { width, height };
    },
    imageFormatDefault: canUseIndexedColor ? 'indexed8' : 'argb8888',
    imagesFormatDefault: canUseIndexedColor ? 'truecolor_alpha' : 'argb8888',
    imageArrayEmptyEnabled: !!canUse(
      'shape-image-array-empty',
      description.deviceType
    ).valid,
    isAndroidDevice: watchfaceStore.watchfaceDevice?.watchOS == 'android',
    isMultiSprite: canUse('multi-sprite-video', description.deviceType).valid
      ? true
      : false
  };
  const root = getRootAst();
  if (!root) return;

  // 获取表盘整体压缩方式，后续图片和图组也使用该值
  buildAstState.data.compressMethod = root.attrsMap['compressMethod'];

  if (watchfaceStore.$state.powerConsumptionLevel != undefined)
    root.attrsMap['powerConsumptionLevel'] = String(
      watchfaceStore.$state.powerConsumptionLevel
    );
  const resourceAst = root.children[0];

  // 添加多语言翻译
  // 在编辑器内部使用不需要多语言，导出时需要
  if (
    buildAstState.options.buildFor == 'export' &&
    buildAstState.data.locales.length > 1
  ) {
    const watchfaceNameTranslations =
      translationStore.translations['watchfaceName'];
    const ast = addWatchfaceNameTranslation(
      resourceAst,
      watchfaceNameTranslations ?? {}
    );
    if (ast) {
      root.attrsMap.name = '@' + ast.attrsMap.name;
    }
  }

  const rootColorGroup: string[] = [];
  const _generateThemeAst = (theme: StoreThemeItem) => {
    // 如果是AOD主题且没有子元素，则不生成
    if (
      buildAstState.options.exportForApp &&
      theme.type == 'AOD' &&
      !theme.children?.length
    )
      return;

    buildAstState.data.theme = theme;
    buildAstState.data.colorTable = [];

    const shapes = Shape.shapeTree(theme.id, false);
    const themeAst: Ast = {
      tag: 'Theme',
      attrsMap: {
        type: theme.type,
        name: theme.name,
        bgColor: toArgb(theme.bgColor),
        isPhotoAlbumWatchface: 'false',
        _id: theme.id
      },
      children: []
    };

    if (theme.type == 'normal') {
      // 添加预览图
      _addThemePreview(theme, themeAst, resourceAst);
    }

    // 处理配色
    const colorTable = getAstColorTable(theme);
    buildAstState.data.colorTable = colorTable;

    // 添加color table并获取实际使用的colorTable
    const colorTable2 = _addColorTable(themeAst, colorTable, colorMode);
    if (colorTable2) rootColorGroup.push(...colorTable2);

    // 此处代码需要在配色处理后执行，为了生成全部样式的配色
    // 在编辑器内使用时，无需处理每个theme的图层
    // 如果传入了待处理的theme,则仅处理该theme，忽略其他的
    // 判断处理哪个theme
    const { themeName, themeType } = options ?? {};
    if (
      buildAstState.options.buildFor == 'editor' &&
      themeName &&
      themeType &&
      // 忽略其他的theme
      (theme.name != themeName || themeType != theme.type)
    ) {
      return;
    }

    // 添加图层
    const shapeLayoutAst = [
      ...shapesToLayoutAst(resourceAst, theme.children, undefined)
    ];

    if (theme.ifttts?.length) {
      themeAst.children = animationToLayoutAst(
        resourceAst,
        theme.ifttts,
        shapeLayoutAst
      );
    } else {
      themeAst.children = shapeLayoutAst;
    }

    // 添加默认背景图
    if (options.addDefaultBgImage) addThemeDefaultBg(themeAst, resourceAst);

    // 处理相册或人像表盘背景、文字表盘、视频表盘
    // 仅在normal下处理
    if (theme.type == 'normal') {
      if (['portrait', 'photoAlbum'].includes(watchfaceType)) {
        _handlePhotoAlbumOrPortraitTheme(themeAst, resourceAst, shapes);
      } else if (watchfaceType == 'videoWatchface') {
        _handleVideoWatchface(themeAst, resourceAst, shapes);
      } else if (watchfaceType == 'textWatchface') {
        _handleTextwatchfaceTheme(themeAst, resourceAst, shapes);
      }
    }
    root.children.push(themeAst);
  };

  // 先处理第一个亮屏和息屏
  const firstNormalTheme = themes.find((item) => item.type == 'normal');
  const firstAODTheme = themes.find(
    (item) => item.name == firstNormalTheme?.name && item.type == 'AOD'
  );
  if (firstNormalTheme) _generateThemeAst(firstNormalTheme);
  if (firstNormalTheme && firstAODTheme) _generateThemeAst(firstAODTheme);
  // 然后依次处理剩余的表盘theme
  themes
    .filter((item) => item.name !== firstNormalTheme?.name)
    .forEach((theme) => {
      _generateThemeAst(theme);
    });

  _addColorTable(root, rootColorGroup, colorMode);

  // 为减少时间，只有导出时处理
  if (buildAstState.options.buildFor == 'export') {
    // 统一timeSecond
    unifyTimeSecond(root);

    // // 删除未用到元素
    // delUslessingResource(root);

    // 移除widget对应的Image资源的colorGroup属性
    removeColorGroupWithWidgetImageResource(resourceAst);

    root.attrsMap.support_literal = addSupportLiteral(resourceAst);
    addSupportGame(root, resourceAst);
    // 删除xml中自定义的属性
    if (!buildAstState.options.keepUslessAttr) removeCustomAttributes(root);

    // 删除相同的layout
    removeDuplicateLayout(root);

    // 最后进行排序
    sortResource(resourceAst);

    root.attrsMap.editable = addEditable(root, resourceAst, rootColorGroup);
    root.attrsMap.interactive = addInteractive(themes);
  }

  // 最后执行
  buildAstState.data = {} as BuildAstData;
  buildAstState.options = {} as BuildAstOptions;
  return root;
};

// 添加editable标签
const addEditable = (root: Ast, resourceAst: Ast, rootColorGroup: string[]) => {
  /**
   * 多样式editable为true
   **/
  const isMultiTheme =
    root.children.filter(
      (theme) => theme.tag === 'Theme' && theme.attrsMap.type === 'normal'
    ).length > 1;

  const slotTypeWidgetCounts = resourceAst.children
    .filter((e) => e.tag == 'Slot')
    .filter((slot) => slot.attrsMap.type === 'widget');

  const slotTypeWidgetOneChildCounts = resourceAst.children
    .filter((e) => e.tag == 'Slot')
    .filter((slot) => slot.attrsMap.type === 'widget')
    .filter((item) => item.children && item.children.length === 1);
  /**
   * 有Slot标签editable为true
   * type为widget的Slot的子元素都为1时，Slot不可编辑
   **/
  const isExistSlot =
    resourceAst.children
      .filter((e) => e.tag == 'Slot')
      .filter((slot) => slot.attrsMap.type !== 'condition').length > 0 &&
    slotTypeWidgetCounts.length !== slotTypeWidgetOneChildCounts.length;

  /**
   * 有recolorTable或colorGroupTable editable为true
   **/
  const isRecolor = root.children.find(
    (item) =>
      item.tag == 'Theme' && item.attrsMap.recolorTable?.split(',')?.length > 1
  );

  const isExistColorGroup = rootColorGroup.length > 0;

  if (isMultiTheme || isExistSlot || isRecolor || isExistColorGroup) {
    return 'true';
  } else {
    return 'false';
  }
};

// 添加文字表盘可编辑属性
const addSupportLiteral = (resourceAst: Ast) => {
  const isExistLiteral = resourceAst.children.find(
    (item) => item.tag == 'DataItemText' && item.attrsMap._type === 'watchface'
  );
  if (!isExistLiteral) return 'false';
  return 'true';
};

const addSupportGame = (root: Ast, resourceAst: Ast) => {
  const isExistApk = resourceAst.children.find(
    (item) =>
      item.tag == 'File' &&
      (item.attrsMap.fileName.endsWith('.apk') ||
        item.attrsMap.fileName.endsWith('.rpk'))
  );
  if (!isExistApk) return 'false';
  delete root.attrsMap.SKU;
  root.attrsMap.support_game = 'true';
  return 'true';
};

const addInteractive = (themes: StoreThemeItem[]) => {
  const interactive = themes.some((theme) => {
    return theme.children.some((shape) => {
      if (shape.type !== 'Rive') return false;
      const interactiveState = ['interactiveStateExt', 'interactiveState'].some(
        (key) => {
          return shape.attrs[key] === true;
        }
      );
      return interactiveState;
    });
  });
  return interactive ? 'true' : 'false';
};
